import {ServerResponse} from 'http';
import {IStateContent} from './reducer';
import {Badge, IEpisodeSort} from '../../common/type';
import {
  createAction,
  createActionFetch,
  createBasicActions,
} from '../../common/util/redux';
import {IEpisodeType, IHistoryTab} from '../../commonSub/type';
import {ISearchType} from '../../search/type';
import {
  IContent,
  IContentAdBanner,
  IContentAddedTicketItem,
  IContentTabV4,
  IContentTicket,
  IContentTicketInfo,
  IContentUsedTicketItem,
  IEpisode,
  IKeywordContent,
} from '../type';

export enum ActionType {
  SetValue = 'content.SetValue',
  SetPropertyValues = 'content.SetPropertyValues',
  SetContent = 'content.SetContent',
  SetContentAdditional = 'content.SetContentAdditional',
  AddEpisode = 'content.AddEpisode',
  RemoveEpisode = 'content.RemoveEpisode',
  SetEpisodeRead = 'content.SetEpisodeRead',
  ReceiveGiftTicket = 'content.ReceiveGiftTicket',
  AddKeywordContents = 'content.AddKeywordContents',
  InitializeAdditional = 'content.InitializeAdditional',
  InitializeTab = 'content.InitializeTab',
  RemoveValues = 'content.RemoveValues',
  FetchContent = 'content.FetchContent',
  FetchContentProfile = 'content.FetchContentProfile',
  FetchContentAdditional = 'content.FetchContentAdditional',
  FetchEpisode = 'content.FetchEpisode',
  FetchReceiveGiftTicket = 'content.FetchReceiveGiftTicket',
  FetchDeleteReadHistory = 'content.FetchDeleteReadHistory',
  FetchPurchaseInfo = 'content.FetchPurchaseInfo',
  FetchPurchaseVoucher = 'content.FetchPurchaseVoucher',
  FetchKeywordContents = 'content.FetchKeywordContents',
  FetchHasRedDot = 'content.FetchHasRedDot',
  FetchContentTicketHistory = 'content.FetchContentTicketHistory',
  AddContentTicketContents = 'content.AddContentTicketContents',
  FetchContentTicketDetail = 'content.FetchContentTicketDetail',
  FetchContentTicketRefund = 'content.FetchContentTicketRefund',
  FetchIsFirstRead = 'content.FetchIsFirstRead',
  FetchViewData = 'content.FetchViewData',
  FetchEpisodeRecommendation = 'content.FetchEpisodeRecommendation',
  FetchRecentNotice = 'content.FetchRecentNotice',
  FetchReadFirstEpisode = 'content.FetchReadFirstEpisode',
  OnFetchImageFinished = 'content.OnFetchImageFinished',
  SetImageUrl = 'content.SetImageUrl',
  FetchFreeTicket = 'content.FetchFreeTicket',
}

export const actions = {
  ...createBasicActions<IStateContent>({
    setValueActionType: ActionType.SetValue,
    setPropertyValuesActionType: ActionType.SetPropertyValues,
    removeValuesActionType: ActionType.RemoveValues,
  }),
  setContent: ({content}: {content: IContent}) =>
    createAction(ActionType.SetContent, {
      content,
    }),
  setContentAdditional: (payload: {
    contentTicket?: IContentTicket;
    giftTicketCount?: number;
    contentAdBanner?: IContentAdBanner;
  }) => createAction(ActionType.SetContentAdditional, payload),
  addEpisode: ({
    contentId,
    isFirst,
    episode,
  }: {
    contentId: number;
    isFirst?: boolean;
    episode: IEpisode;
  }) => createAction(ActionType.AddEpisode, {contentId, isFirst, episode}),
  removeEpisode: (contentId: number) =>
    createAction(ActionType.RemoveEpisode, {contentId}),
  setEpisodeRead: (payload: {
    contentId: number;
    episodeId: number;
    type: IEpisodeType;
    timeText: string;
    badge?: Badge;
  }) => createAction(ActionType.SetEpisodeRead, payload),
  receiveGiftTicket: (payload: {
    id: number;
    giftTicketCount: number;
    isCancelled?: boolean;
  }) => createAction(ActionType.ReceiveGiftTicket, payload),
  initializeAdditional: () => createAction(ActionType.InitializeAdditional),
  initializeTab: () => createAction(ActionType.InitializeTab),
  fetchContent: (payload: {id: number; res?: ServerResponse}) =>
    createActionFetch(ActionType.FetchContent, {
      payload,
    }),
  fetchContentAdditional: (payload: {id: number}) =>
    createActionFetch(ActionType.FetchContentAdditional, {
      payload,
    }),
  AddKeywordContents: (contents: IKeywordContent[], isFirst?: boolean) =>
    createAction(ActionType.AddKeywordContents, {
      contents,
      isFirst,
    }),
  fetchContentProfile: (id: number) =>
    createActionFetch(ActionType.FetchContentProfile, {
      payload: {id},
      fetchKey: id,
    }),
  fetchEpisode: (
    payload: {
      contentId: number;
      sort: IEpisodeSort;
    },
    isFirst?: boolean
  ) =>
    createActionFetch(ActionType.FetchEpisode, {
      payload,
      fetchKey: payload.contentId,
      isFirst,
    }),
  fetchReceiveGiftTicket: (payload: {
    id: number;
    title: string;
    giftTicketCount: number;
  }) => createActionFetch(ActionType.FetchReceiveGiftTicket, {payload}),
  fetchDeleteReadHistory: (payload: {id: number}) =>
    createActionFetch(ActionType.FetchDeleteReadHistory, {
      payload,
    }),
  fetchPurchaseInfo: (payload: {id: number; sid: string}) =>
    createActionFetch(ActionType.FetchPurchaseInfo, {
      payload,
    }),
  fetchPurchaseVoucher: (payload: {
    serverData: any;
    ticketId: number;
    isBuyAtOnce: boolean;
    price: number;
    bundleCount: number;
    sid: string;
    id: number;
    isBuy: boolean;
    contentTitle: string;
    isDiscount: boolean;
    isShowLatestInfo?: boolean;
  }) => createActionFetch(ActionType.FetchPurchaseVoucher, {payload}),
  fetchKeywordContents: (
    payload: {keyword: string; type?: ISearchType},
    isFirst?: boolean
  ) =>
    createActionFetch(ActionType.FetchKeywordContents, {
      payload,
      isFirst,
      fetchKey: payload.keyword,
    }),
  fetchHasRedDot: (payload: {contentId: number}) =>
    createActionFetch(ActionType.FetchHasRedDot, {
      payload,
    }),
  fetchContentTicketHistory: (
    {tab, id}: {tab: IHistoryTab; id?: number},
    isFirst?: boolean
  ) =>
    createActionFetch(ActionType.FetchContentTicketHistory, {
      payload: {tab, id},
      fetchKey: tab,
      isFirst,
    }),
  addContentTicketContents: (
    {
      tab,
      ticketInfo,
      contents,
    }:
      | {
          tab: 'added';
          ticketInfo: IContentTicketInfo;
          contents: IContentAddedTicketItem[];
        }
      | {
          tab: 'used';
          ticketInfo?: undefined;
          contents: IContentUsedTicketItem[];
        },
    isFirst?: boolean
  ) =>
    createAction(ActionType.AddContentTicketContents, {
      tab,
      ticketInfo,
      contents,
      isFirst,
    }),
  fetchContentTicketDetail: (ticketId: number) =>
    createActionFetch(ActionType.FetchContentTicketDetail, {
      payload: {ticketId},
    }),
  fetchContentTicketRefund: (data: {
    id: number;
    ticketId: number;
    sid: string;
  }) =>
    createActionFetch(ActionType.FetchContentTicketRefund, {
      payload: data,
    }),
  fetchIsFirstRead: (payload: {contentId: number}) =>
    createActionFetch(ActionType.FetchIsFirstRead, {
      payload,
    }),
  fetchViewData: (payload: {contentId: number; episodeId?: number}) =>
    createActionFetch(ActionType.FetchViewData, {
      payload,
    }),
  fetchEpisodeRecommendation: (contentId: number) =>
    createActionFetch(ActionType.FetchEpisodeRecommendation, {
      payload: {contentId},
    }),
  fetchRecentNotice: (contentId: number) =>
    createActionFetch(ActionType.FetchRecentNotice, {
      payload: {contentId},
    }),
  fetchReadFirstEpisode: (payload: {contentId: number; episodeId: number}) =>
    createActionFetch(ActionType.FetchReadFirstEpisode, {payload}),
  onFetchImageFinished: () => createAction(ActionType.OnFetchImageFinished),
  setImageUrl: (data: {index: number; url: string; episodeId: number}) =>
    createAction(ActionType.SetImageUrl, data),
  fetchFreeTicket: ({
    contentId,
    contentTitle,
    tab,
  }: {
    contentId: number;
    contentTitle: string;
    tab?: IContentTabV4;
  }) =>
    createActionFetch(ActionType.FetchFreeTicket, {
      payload: {contentId, contentTitle, tab},
    }),
};
